"use client";

import { useCallback, useMemo } from "react";
import { useRouter, useSearchParams, usePathname, useParams } from "next/navigation";
import { useTranslations } from "next-intl";
import type { SortOption, ActiveFilter } from "./filter-bar";
import type { ProductOrPeptideCard } from "./filter-utils";
import {
	extractColorOptions,
	extractSizeOptions,
	extractCategoryOptions,
	extractPeptideCategoryOptions,
	extractResearchLevelOptions,
	extractAvailabilityOptions,
	getStaticPriceRangesWithCount,
	getCurrencySymbol,
	filterProducts,
	filterProductsByPeptide,
	buildActiveFilters,
	sortProductsClientSide,
	normalizeAvailability,
	normalizeSortOption,
	type CategoryOption,
} from "./filter-utils";

interface UseProductFiltersOptions {
	products: ProductOrPeptideCard[];
	/** Categories resolved from URL slugs (server-side) for active filter display */
	resolvedCategories?: Array<{ slug: string; id: string; name: string }>;
	/** Whether to include category filter (only for /products page) */
	enableCategoryFilter?: boolean;
}

interface UseProductFiltersResult {
	// Filtered/sorted products
	filteredProducts: ProductOrPeptideCard[];

	// Filter options for UI
	categoryOptions: CategoryOption[];
	colorOptions: Array<{ name: string; count: number; hex?: string }>;
	sizeOptions: Array<{ name: string; count: number }>;
	priceRanges: Array<{ label: string; value: string; count: number }>;
	peptideCategoryOptions: { name: string; count: number }[];
	researchLevelOptions: string[];
	availabilityOptions: string[];

	// Selected filter values
	selectedCategories: string[];
	selectedColors: string[];
	selectedSizes: string[];
	selectedPriceRange: string | null;
	searchQuery: string;
	selectedPeptideCategories: string[];
	selectedResearchLevel: string;
	selectedAvailability: string;
	sortValue: SortOption;

	// Active filters for display
	activeFilters: ActiveFilter[];

	// Handlers
	handleCategoryToggle: (slug: string) => void;
	handleColorToggle: (color: string) => void;
	handleSizeToggle: (size: string) => void;
	handlePriceRangeChange: (range: string | null) => void;
	handleSearchChange: (query: string) => void;
	handlePeptideCategoryToggle: (name: string) => void;
	handleResearchLevelChange: (level: string) => void;
	handleAvailabilityChange: (availability: string) => void;
	handleSortChange: (sort: SortOption) => void;
	handleRemoveFilter: (key: string, value: string) => void;
	handleClearFilters: () => void;
}

/**
 * Custom hook for product filtering and sorting logic.
 * Consolidates filter state management, URL synchronization, and filter application.
 *
 * Server-side filters: categories, price (via URL params -> GraphQL)
 * Client-side filters: colors, sizes (via JavaScript filtering)
 */
export function useProductFilters({
	products,
	resolvedCategories = [],
	enableCategoryFilter = false,
}: UseProductFiltersOptions): UseProductFiltersResult {
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const { channel = "standard" } = useParams<{ channel: string }>();
	const t = useTranslations("plp");

	// Parse current filters from URL
	const selectedCategories = useMemo(
		() => (enableCategoryFilter ? searchParams.get("categories")?.split(",").filter(Boolean) || [] : []),
		[searchParams, enableCategoryFilter],
	);
	const selectedColors = useMemo(
		() => searchParams.get("colors")?.split(",").filter(Boolean) || [],
		[searchParams],
	);
	const selectedSizes = useMemo(
		() => searchParams.get("sizes")?.split(",").filter(Boolean) || [],
		[searchParams],
	);
	const selectedPriceRange = searchParams.get("price") || null;
	const searchQuery = searchParams.get("q") || "";
	const selectedPeptideCategories = useMemo(
		() => searchParams.get("peptideCategories")?.split(",").filter(Boolean) || [],
		[searchParams],
	);
	const selectedResearchLevel = searchParams.get("researchLevel") || "all";
	const selectedAvailability = normalizeAvailability(searchParams.get("availability"));
	const sortValue = normalizeSortOption(searchParams.get("sort"));

	// Update URL with new filters (triggers server re-fetch for server-side filters)
	const updateFilters = useCallback(
		(updates: {
			categories?: string[];
			colors?: string[];
			sizes?: string[];
			price?: string | null;
			sort?: string;
			q?: string;
			peptideCategories?: string[];
			researchLevel?: string;
			availability?: string;
		}) => {
			const params = new URLSearchParams(searchParams.toString());

			if (updates.categories !== undefined) {
				if (updates.categories.length > 0) {
					params.set("categories", updates.categories.join(","));
				} else {
					params.delete("categories");
				}
			}

			if (updates.colors !== undefined) {
				if (updates.colors.length > 0) {
					params.set("colors", updates.colors.join(","));
				} else {
					params.delete("colors");
				}
			}

			if (updates.sizes !== undefined) {
				if (updates.sizes.length > 0) {
					params.set("sizes", updates.sizes.join(","));
				} else {
					params.delete("sizes");
				}
			}

			if (updates.price !== undefined) {
				if (updates.price) {
					params.set("price", updates.price);
				} else {
					params.delete("price");
				}
			}

			const sort = normalizeSortOption(updates.sort ?? params.get("sort"));
			if (sort !== "featured") {
				params.set("sort", sort);
			} else {
				params.delete("sort");
			}

			if (updates.q !== undefined) {
				if (updates.q.trim()) {
					params.set("q", updates.q.trim());
				} else {
					params.delete("q");
				}
			}

			if (updates.peptideCategories !== undefined) {
				if (updates.peptideCategories.length > 0) {
					params.set("peptideCategories", updates.peptideCategories.join(","));
				} else {
					params.delete("peptideCategories");
				}
			}

			if (updates.researchLevel !== undefined) {
				if (updates.researchLevel && updates.researchLevel !== "all") {
					params.set("researchLevel", updates.researchLevel);
				} else {
					params.delete("researchLevel");
				}
			}

			const availability = normalizeAvailability(updates.availability ?? params.get("availability"));
			if (availability !== "all") {
				params.set("availability", availability);
			} else {
				params.delete("availability");
			}

			const queryString = params.toString();
			router.push(queryString ? `${pathname}?${queryString}` : pathname, { scroll: false });
		},
		[router, pathname, searchParams],
	);

	// Filter handlers
	const handleCategoryToggle = useCallback(
		(slug: string) => {
			const newCategories = selectedCategories.includes(slug)
				? selectedCategories.filter((c) => c !== slug)
				: [...selectedCategories, slug];
			updateFilters({ categories: newCategories });
		},
		[selectedCategories, updateFilters],
	);

	const handleColorToggle = useCallback(
		(color: string) => {
			const newColors = selectedColors.includes(color)
				? selectedColors.filter((c) => c !== color)
				: [...selectedColors, color];
			updateFilters({ colors: newColors });
		},
		[selectedColors, updateFilters],
	);

	const handleSizeToggle = useCallback(
		(size: string) => {
			const newSizes = selectedSizes.includes(size)
				? selectedSizes.filter((s) => s !== size)
				: [...selectedSizes, size];
			updateFilters({ sizes: newSizes });
		},
		[selectedSizes, updateFilters],
	);

	const handlePriceRangeChange = useCallback(
		(range: string | null) => {
			updateFilters({ price: range });
		},
		[updateFilters],
	);

	const handleSearchChange = useCallback(
		(query: string) => {
			updateFilters({ q: query });
		},
		[updateFilters],
	);

	const handlePeptideCategoryToggle = useCallback(
		(name: string) => {
			const newCats = selectedPeptideCategories.includes(name)
				? selectedPeptideCategories.filter((c) => c !== name)
				: [...selectedPeptideCategories, name];
			updateFilters({ peptideCategories: newCats });
		},
		[selectedPeptideCategories, updateFilters],
	);

	const handleResearchLevelChange = useCallback(
		(level: string) => {
			updateFilters({ researchLevel: level });
		},
		[updateFilters],
	);

	const handleAvailabilityChange = useCallback(
		(availability: string) => {
			updateFilters({ availability });
		},
		[updateFilters],
	);

	const handleSortChange = useCallback(
		(sort: SortOption) => {
			updateFilters({ sort });
		},
		[updateFilters],
	);

	// Extract filter options from products
	const categoryOptions = useMemo(() => extractCategoryOptions(products), [products]);
	const peptideCategoryOptions = useMemo(() => extractPeptideCategoryOptions(products), [products]);
	const researchLevelOptions = useMemo(() => extractResearchLevelOptions(products), [products]);
	const availabilityOptions = useMemo(() => extractAvailabilityOptions(products), [products]);

	const handleRemoveFilter = useCallback(
		(key: string, value: string) => {
			if (key === "category") {
				const category =
					resolvedCategories.find((c) => c.name === value) || categoryOptions.find((c) => c.name === value);
				if (category) {
					const newCategories = selectedCategories.filter((c) => c !== category.slug);
					updateFilters({ categories: newCategories });
				}
			} else if (key === "color") {
				const newColors = selectedColors.filter((c) => c !== value);
				updateFilters({ colors: newColors });
			} else if (key === "size") {
				const newSizes = selectedSizes.filter((s) => s !== value);
				updateFilters({ sizes: newSizes });
			} else if (key === "price") {
				updateFilters({ price: null });
			} else if (key === "search") {
				updateFilters({ q: "" });
			} else if (key === "peptideCategory") {
				const newCats = selectedPeptideCategories.filter((c) => c !== value);
				updateFilters({ peptideCategories: newCats });
			} else if (key === "researchLevel") {
				updateFilters({ researchLevel: "all" });
			} else if (key === "availability") {
				updateFilters({ availability: "all" });
			}
		},
		[
			resolvedCategories,
			categoryOptions,
			selectedCategories,
			selectedColors,
			selectedSizes,
			selectedPeptideCategories,
			updateFilters,
		],
	);

	const handleClearFilters = useCallback(() => {
		router.push(pathname, { scroll: false });
	}, [router, pathname]);

	// Extract filter options with selected values included for deselection
	const colorOptions = useMemo(
		() => extractColorOptions(products, selectedColors),
		[products, selectedColors],
	);
	const sizeOptions = useMemo(() => extractSizeOptions(products, selectedSizes), [products, selectedSizes]);

	// Apply client-side filters: colors/sizes, then peptide filters
	const clientFilteredProducts = useMemo(() => {
		let filtered = filterProducts(products, {
			colors: selectedColors,
			sizes: selectedSizes,
		});
		filtered = filterProductsByPeptide(filtered, {
			searchQuery,
			peptideCategories: selectedPeptideCategories.length > 0 ? selectedPeptideCategories : undefined,
			researchLevel: selectedResearchLevel !== "all" ? selectedResearchLevel : undefined,
			availability: selectedAvailability !== "all" ? selectedAvailability : undefined,
		});
		return filtered;
	}, [
		products,
		selectedColors,
		selectedSizes,
		searchQuery,
		selectedPeptideCategories,
		selectedResearchLevel,
		selectedAvailability,
	]);

	// Sort (server-side primary, client-side fallback)
	const filteredProducts = useMemo(
		() => sortProductsClientSide(clientFilteredProducts, sortValue),
		[clientFilteredProducts, sortValue],
	);

	// Build active filters for display
	const activeFilters = useMemo(() => {
		const filters = buildActiveFilters({
			colors: selectedColors,
			sizes: selectedSizes,
			priceRange: selectedPriceRange,
			currencySymbol: getCurrencySymbol(channel),
		});

		resolvedCategories.forEach((cat) => {
			filters.unshift({ key: "category", label: t("category"), value: cat.name });
		});

		if (searchQuery.trim()) {
			filters.push({ key: "search", label: t("searchLabel"), value: searchQuery });
		}
		selectedPeptideCategories.forEach((cat) => {
			filters.push({ key: "peptideCategory", label: t("peptideCategoryLabel"), value: cat });
		});
		if (selectedResearchLevel !== "all") {
			filters.push({ key: "researchLevel", label: t("researchLevel"), value: selectedResearchLevel });
		}
		if (selectedAvailability !== "all") {
			filters.push({ key: "availability", label: t("availability"), value: selectedAvailability });
		}

		return filters;
	}, [
		selectedColors,
		selectedSizes,
		selectedPriceRange,
		resolvedCategories,
		searchQuery,
		selectedPeptideCategories,
		selectedResearchLevel,
		selectedAvailability,
		channel,
		t,
	]);

	return {
		filteredProducts,
		categoryOptions,
		colorOptions,
		sizeOptions,
		priceRanges: getStaticPriceRangesWithCount(channel),
		peptideCategoryOptions,
		researchLevelOptions,
		availabilityOptions,
		selectedCategories,
		selectedColors,
		selectedSizes,
		selectedPriceRange,
		searchQuery,
		selectedPeptideCategories,
		selectedResearchLevel,
		selectedAvailability,
		sortValue,
		activeFilters,
		handleCategoryToggle,
		handleColorToggle,
		handleSizeToggle,
		handlePriceRangeChange,
		handleSearchChange,
		handlePeptideCategoryToggle,
		handleResearchLevelChange,
		handleAvailabilityChange,
		handleSortChange,
		handleRemoveFilter,
		handleClearFilters,
	};
}
